import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCart } from '../lib/cart';
import { X, Plus, Minus, ShoppingBag, Trash2 } from 'lucide-react';
import api from '../lib/api';
import { resolveImg } from '../lib/img';

export default function CartDrawer() {
  const { t, i18n } = useTranslation();
  const lng = i18n.language?.startsWith('en') ? 'en' : 'fr';
  const cart = useCart();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  if (!cart) return null;

  const startCheckout = async () => {
    setBusy(true); setError('');
    try {
      const items = cart.items.map((i) => ({ product_id: i.id, quantity: i.qty }));
      const { data } = await api.post('/checkout/session', {
        items,
        origin: window.location.origin,
        customer_email: email || undefined,
      });
      if (data.url) window.location.href = data.url;
    } catch (e) {
      const d = e.response?.data?.detail;
      setError(typeof d === 'string' ? d : t('shop.checkout_error'));
    } finally { setBusy(false); }
  };

  return (
    <>
      {cart.open && (
        <div className="fixed inset-0 z-[80] bg-ink/60 backdrop-blur-sm" onClick={() => cart.setOpen(false)} data-testid="cart-overlay" />
      )}
      <aside
        className={`fixed top-0 right-0 z-[90] h-full w-full sm:max-w-md bg-bone shadow-2xl transition-transform duration-400 ${
          cart.open ? 'translate-x-0' : 'translate-x-full'
        }`}
        data-testid="cart-drawer"
      >
        <div className="h-full flex flex-col">
          <div className="flex items-center justify-between px-6 py-5 border-b border-border-soft">
            <div className="flex items-center gap-3">
              <span className="grid place-items-center w-9 h-9 rounded-full bg-leaf/15 text-leaf">
                <ShoppingBag size={18} />
              </span>
              <h2 className="font-heading text-2xl text-ink">{t('shop.cart')}</h2>
            </div>
            <button onClick={() => cart.setOpen(false)} className="text-ink-muted hover:text-ink" data-testid="cart-close" aria-label="close">
              <X />
            </button>
          </div>

          <div className="flex-1 overflow-auto px-6 py-5">
            {cart.items.length === 0 && (
              <div className="text-center py-16">
                <p className="text-ink-muted">{t('shop.cart_empty')}</p>
                <Link to="/shop" onClick={() => cart.setOpen(false)} className="mt-4 inline-block text-forest border-b border-forest/40 pb-0.5">
                  {t('shop.back_to_shop')}
                </Link>
              </div>
            )}
            {cart.items.map((it) => (
              <div key={it.id} className="flex gap-4 py-4 border-b border-border-soft" data-testid={`cart-item-${it.id}`}>
                {it.image && <img src={resolveImg(it.image)} alt="" className="w-20 h-20 rounded-xl object-cover" />}
                <div className="flex-1 min-w-0">
                  <p className="font-heading text-lg text-ink">{it.name}</p>
                  {it.weight_label && <p className="text-xs text-ink-muted">{it.weight_label}</p>}
                  <div className="mt-2 flex items-center gap-2">
                    <button onClick={() => cart.setQty(it.id, it.qty - 1)} className="w-7 h-7 grid place-items-center rounded-full border border-border-soft hover:bg-sand"><Minus size={12} /></button>
                    <span className="text-sm w-6 text-center" data-testid={`cart-qty-${it.id}`}>{it.qty}</span>
                    <button onClick={() => cart.setQty(it.id, it.qty + 1)} className="w-7 h-7 grid place-items-center rounded-full border border-border-soft hover:bg-sand"><Plus size={12} /></button>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-heading text-lg text-forest">{(it.price_eur * it.qty).toFixed(2)} €</p>
                  <button onClick={() => cart.remove(it.id)} className="text-red-600 mt-2" data-testid={`cart-remove-${it.id}`}><Trash2 size={14} /></button>
                </div>
              </div>
            ))}
          </div>

          {cart.items.length > 0 && (
            <div className="border-t border-border-soft p-6 grid gap-4">
              <div className="grid gap-1.5">
                <label className="overline">{t('shop.email_label')}</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('shop.email_ph')}
                  data-testid="cart-email"
                  className="rounded-xl border border-border-soft bg-white px-4 py-2.5 outline-none focus:border-forest text-sm"
                />
              </div>
              <div className="flex justify-between items-baseline">
                <span className="text-ink-soft">{t('shop.total')}</span>
                <span className="font-heading text-3xl text-forest" data-testid="cart-total">{cart.total.toFixed(2)} €</span>
              </div>
              {error && <p className="text-red-700 text-sm" data-testid="cart-error">{error}</p>}
              <button
                onClick={startCheckout}
                disabled={busy || !email}
                className="btn-primary w-full disabled:opacity-60"
                data-testid="cart-checkout"
              >
                {busy ? t('common.loading') : t('shop.checkout')}
              </button>
              <p className="text-xs text-ink-muted text-center">{t('shop.secure_payment')}</p>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}
